import { Fragment, useEffect, useState } from 'react';
import { useEssayThemes } from '../../hooks/useEssayThemes';
import { useThemeListStore } from '../../stores/themeListStore';
import { Typography } from '../../../../core/components/molecules/Typography';
import { Button } from '../../../../core/components/atoms/Button';
import { Icon } from '../../../../core/components/atoms/Icon';
import { IListedTheme } from '../../../../core/models/essay';
import { useNavigate } from 'react-router-dom';
import { essayTitle } from '../../utils';
import { Input } from '../../../../core/components/atoms/Input';
import { Alert } from '../../../../core/components/atoms/Alert';
import { Tag } from '../../../../core/components/atoms/Tag';
import { meEssayStore } from '../../stores/meEssayStore';
import { useCredits } from '../../hooks/useCredits';
import { Chip } from '../../../../core/components/atoms/Chip';
import FilterTheme from './components/modalFiltro';
import ContentLoader from 'react-content-loader';
import PageHeader from '../../../../core/components/molecules/Header';
import Container from '../../../../core/components/atoms/Container';
import ContentListCard from '../../../../core/components/organism/ContentListCard';
import { useThemeFilterStore } from '../../stores/themeFilterStore';
import CorrectionInfoModal from '../../components/CorrectionInfoModal';
import { AboutThemesModal } from './components/AboutThemesModal';
import { MODAL_VIDEOS_VIMEO_ALLOWED } from '../../../../core/constants/modal-videos';

export default function ThemesPage() {
  const navigate = useNavigate();
  const { mutate, isPending } = useEssayThemes();
  useCredits();
  const { themes } = useThemeListStore();
  const { credits } = meEssayStore();
  const { reset } = useThemeFilterStore();

  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [openInfo, setOpenInfo] = useState<boolean>(false);
  const [searchName, setSearchName] = useState<string | undefined>(undefined);
  const [data, setData] = useState<IListedTheme[]>([]);

  const [isAboutThemesModalOpen, setIsAboutThemesModalOpen] = useState(false);

  useEffect(() => {
    mutate();
  }, []);

  useEffect(() => {
    //const today = new Date();

    // const filteredThemes = themes.filter(theme => {
    //   if (!theme.startDate || !theme.endDate) return theme.display !== false;

    //   const start = new Date(theme.startDate);
    //   const end = new Date(theme.endDate);

    //   return (start <= today && today <= end) && theme.display !== false;
    // });

    setData(themes);
  }, [themes]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (searchName && searchName.length > 0) {
        const filter = data.filter(
          (it) =>
            it.display !== false &&
            (it.theme.toLowerCase().includes(searchName.toLowerCase()) ||
              it.secondaryGenre
                ?.toLocaleLowerCase()
                .includes(searchName.toLocaleLowerCase()) ||
              it.mainGenre
                ?.toLocaleLowerCase()
                .includes(searchName.toLocaleLowerCase()))
        );

        setData(filter);
      } else {
        const today = new Date();
        const filteredThemes = themes.filter((theme) => {
          if (!theme.startDate || !theme.endDate)
            return theme.display !== false;

          const start = new Date(theme.startDate);
          const end = new Date(theme.endDate);

          return start <= today && today <= end && theme.display !== false;
        });
        setData(filteredThemes);
      }
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchName]);

  const submitFilter = () => {
    setOpenFilter(false);
    mutate();
  };

  const buildTags = (it: IListedTheme) => {
    if (it) {
      const tags: { text: string; color: string }[] = [];

      tags.push({
        text: essayTitle(it.correctionType),
        color: '',
      });

      if (it.mainGenre) {
        tags.push({ text: it.mainGenre, color: '' });
      }

      if (it.hasWebDoc) {
        tags.push({ text: 'AprovaDoc', color: '' });
      }

      return tags;
    }

    return [];
  };

  return (
    <Container
      gap='xxl'
      direction='col'
      type='updated'
      className='xs:mb-[30px] xl:mb-[30px]'
    >
      <CorrectionInfoModal
        isOpen={openInfo}
        onClose={() => setOpenInfo(false)}
      />
      <FilterTheme
        isOpen={openFilter}
        onClose={() => setOpenFilter(false)}
        onSubmit={() => submitFilter()}
      />
      <div className='flex justify-between xs:flex-row xs:max-lg:items-center lg:flex-col'>
        <div className='flex flex-col gap-4 md:flex-row md:items-center'>
          <PageHeader title='Temas' module='Redação' />
          {MODAL_VIDEOS_VIMEO_ALLOWED.TEMAS_REDACAO.allowed && (
            <Button
              variant='text'
              className='hidden flex-none p-0 text-center text-md md:flex md:text-md'
              LeftIcon='info'
              iconType='material'
              iconSize='small'
              onClick={() => setIsAboutThemesModalOpen(true)}
            >
              Como funciona
            </Button>
          )}
          <AboutThemesModal
            isOpen={isAboutThemesModalOpen}
            onClose={() => setIsAboutThemesModalOpen(false)}
          />
        </div>
        <div className='flex flex-col items-center justify-end gap-4 md:flex-row md:items-start'>
          <div className='flex max-w-[560px] flex-col gap-2'>
            <Tag
              variant={
                credits?.remainingCreditsMonth &&
                credits?.remainingCreditsMonth > 0
                  ? 'neutral'
                  : 'alert'
              }
              className='inline-flex w-fit items-center gap-sm self-end bg-mono-color-white px-3 py-2'
            >
              <Icon
                Icon='folder'
                type='material'
                className='text-md text-mono-color-darkest'
              />
              <Typography className='text-md text-mono-color-darkest'>
                {credits?.remainingCreditsMonth} créditos disponíveis*
              </Typography>
            </Tag>
            <Typography
              size='small'
              className='text-mono-color-darkest xs:text-center md:text-right'
            >
              *Você recebe 4 créditos todo mês. Cada redação enviada para
              correção usa 1 crédito.
            </Typography>
          </div>
        </div>
      </div>
      {MODAL_VIDEOS_VIMEO_ALLOWED.TEMAS_REDACAO.allowed && (
        <Button
          variant='text'
          LeftIcon='info'
          iconType='material'
          iconSize='small'
          className='p-0 text-md md:hidden md:text-md'
          onClick={() => setIsAboutThemesModalOpen(true)}
        >
          Como funciona
        </Button>
      )}
      {credits?.remainingCreditsMonth === 0 && (
        <Alert
          variant='warning'
          message={
            <Typography className='my-1'>
              Atenção: Você não possui créditos disponíveis para enviar
              redações.
              <strong>
                {' '}
                Seus créditos serão renovados no inicio de cada mês{' '}
              </strong>
            </Typography>
          }
        />
      )}
      <div className='flex h-fit w-full flex-row items-center justify-between'>
        <Input
          className='xs:w-2/3 lg:w-1/3'
          onChange={(e) => setSearchName(e.target.value)}
          value={searchName}
          label='Encontrar tema'
          IconLeft={{
            Icon: 'search',
            type: 'material',
            className: 'text-mono-color-dark',
          }}
        />
        <div className='flex items-center gap-md'>
          <Button
            size='small'
            LeftIcon='workspace_premium'
            iconType='material'
            iconSize='small'
            variant='text'
            className='text-button-color-tertiary-default'
            onClick={() => setOpenInfo(true)}
          >
            Controle de qualidade das correções
          </Button>
          <Button
            variant='text'
            onClick={() => setOpenFilter(true)}
            className='p-0'
          >
            <Icon
              Icon='filter_list'
              type='material'
              className='xs:text-sm md:text-md'
            />
            <span className='xs:text-sm md:text-md'>Filtrar temas</span>
          </Button>
        </div>
      </div>
      {isPending ? (
        <div className='grid w-full gap-md xs:grid-cols-1 lg:grid-cols-3 xl:grid-cols-4'>
          {Array.from({ length: 10 }, (_, key) => (
            <div key={key}>
              <ContentLoader
                backgroundColor={'#a7adba'}
                foregroundColor={'#c0c5ce'}
                viewBox='0 0 600 500'
              >
                <rect x='0' y='10' rx='2' ry='2' width='600' height='700' />
              </ContentLoader>
            </div>
          ))}
        </div>
      ) : (
        <Fragment>
          <div className='w-full gap-md xs:hidden xs:grid-cols-1 lg:grid lg:grid-cols-3 xl:grid-cols-4'>
            {data.map((it, i) => (
              <ContentListCard
                key={i}
                text={it.theme}
                onClick={() => {
                  reset();
                  navigate(`/redacoes/start/${it.slug}`);
                }}
                tags={buildTags(it)}
                imageBackground={it.thumbnail}
                hasBand={it.isWeekTheme}
                band={
                  <Typography
                    weight='bold'
                    size='small'
                    className='text-card-color-default'
                  >
                    Tema da semana
                  </Typography>
                }
                color='default'
              />
            ))}
          </div>
          <div className='w-full flex-col gap-md xs:flex lg:hidden'>
            {data.map((it, i) => {
              const tags = buildTags(it);

              return (
                <div
                  key={i}
                  className='flex cursor-pointer flex-col gap-md rounded-md bg-color-lightest p-4'
                  onClick={() => navigate(`/redacoes/start/${it.slug}`)}
                >
                  <div className='flex flex-wrap gap-sm'>
                    {tags?.map((tag, i) => (
                      <Chip
                        key={i}
                        variant='negative'
                        className={`shadow-level-none px-2 ${tag.color} whitespace-nowrap`}
                        clamp
                      >
                        {tag.text}
                      </Chip>
                    ))}

                    {it.axis && (
                      <Chip
                        variant='negative'
                        className='shadow-level-none whitespace-nowrap px-2'
                      >
                        {it.axis}
                      </Chip>
                    )}
                  </div>

                  <Typography type='title' size='h5'>
                    {it.theme}
                  </Typography>
                </div>
              );
            })}
          </div>
        </Fragment>
      )}
    </Container>
  );
}
